import AccordionButton from '@/components/atoms/AccordionButton';
import Button from '@/components/atoms/Button';
import Heading from '@/components/atoms/Heading';
import RouterBackButton from '@/components/atoms/RouterBackButton';
import BreadcrumbLists from '@/components/templates/BreadcrumbLists';
import useGetCampaign from '@/mobile/hooks/api/useGetCampaign';
import isShowCampaign from '@/mobile/logics/campaign/detail';
import { mobileCampaignState } from '@/mobile/states/campaignState';
import { Campaign, Cashback } from '@/mobile/types/api/mypage/campaign';
import { CashbackRegisterFormState } from '@/states/cashbackRegisterState';
import { formatDate } from '@/utils/commonFunc';
import type { NextPage } from 'next';
import Router from 'next/router';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';

/**
 * キャンペーン情報詳細画面
 *
 */
const CampaignDetail: NextPage = () => {
  // 共通state
  const [cashbackRegisterForm, setCashbackRegisterForm] = useRecoilState(CashbackRegisterFormState);
  const resetCashbackRegisterFormState = useResetRecoilState(CashbackRegisterFormState);
  const campaignInfo = useRecoilValue(mobileCampaignState);

  // APIの呼び出し
  const { execute: getCampaign } = useGetCampaign();

  // 「キャンペーン内容」のアコーディオン状態
  const [isOpenCampaignAccordion, setIsOpenCampaignAccordion] = useState(false);

  useEffect(() => {
    resetCashbackRegisterFormState();

    getCampaign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toCashbackRegister = (cashbackCode: string) => {
    setCashbackRegisterForm({
      ...cashbackRegisterForm,
      code: cashbackCode,
    });

    Router.push('/mobile/campaign/cashback/');
  };

  return (
    <main className='l-main'>
      <Heading as='h1'>特典・キャンペーン</Heading>
      <div className='m-box-02'>
        <div className='m-box-02__inner'>
          <div className='m-heading-l2'>
            <h2 className='m-heading-l2__hdg'>キャンペーンの確認</h2>
          </div>
          {/** キャンペーン適用状況 */}
          {isShowCampaign(campaignInfo) && (
            <>
              <div className='m-heading-l3'>
                <div className='accordion_container'>
                  <h3 className='m-heading-l3__hdg'>キャンペーン内容</h3>
                  <AccordionButton
                    label={{ open: '表示', close: '非表示' }}
                    isDisplay={isOpenCampaignAccordion}
                    onClick={() => setIsOpenCampaignAccordion(!isOpenCampaignAccordion)}
                  />
                </div>
              </div>
              {isOpenCampaignAccordion && (
                <>
                  {campaignInfo.cashbacks.map((item: Cashback) => {
                    return (
                      <div key={item.cashback_id}>
                        <table>
                          <colgroup>
                            <col className='w-30' />
                            <col />
                          </colgroup>
                          <tbody>
                            <tr>
                              <th className='m-table__th'>名称</th>
                              <td
                                className='m-table__td'
                                style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                              >
                                {item.name}
                              </td>
                            </tr>
                            <tr>
                              <th className='m-table__th'>内容</th>
                              <td
                                className='m-table__td'
                                style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                              >
                                {item.condition && (
                                  <p>
                                    【適用条件】
                                    <br />
                                    {item.condition}
                                    <br />
                                  </p>
                                )}
                                {item.period_string && (
                                  <p>
                                    【時期／期間】
                                    <br />
                                    {item.period_string}
                                  </p>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        <br />
                      </div>
                    );
                  })}
                  {campaignInfo.campaigns.map((item: Campaign) => {
                    return (
                      <div key={item.name}>
                        <table>
                          <colgroup>
                            <col className='w-30' />
                            <col />
                          </colgroup>
                          <tbody>
                            <tr key={`${item.name}name`}>
                              <th className='m-table__th'>名称</th>
                              <td
                                className='m-table__td'
                                style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                              >
                                {item.name}
                              </td>
                            </tr>
                            <tr key={`${item.name}content`}>
                              <th className='m-table__th'>内容</th>
                              <td
                                className='m-table__td'
                                style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                              >
                                {item.condition && (
                                  <p>
                                    【適用条件】
                                    <br />
                                    {item.condition}
                                    <br />
                                  </p>
                                )}
                                {item.period_string && (
                                  <p>
                                    【時期／期間】
                                    <br />
                                    {item.period_string}
                                  </p>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        <br />
                      </div>
                    );
                  })}
                </>
              )}
            </>
          )}

          {campaignInfo.cashbacks.length !== 0 && (
            <>
              <h3 className='m-heading-l3 m-heading-l3__hdg'>キャッシュバック受取手続き</h3>
              <div className='cashback'>
                {campaignInfo.cashbacks.map((cashback: Cashback) => {
                  return (
                    <div key={cashback.cashback_id} style={{ marginBottom: '32px' }}>
                      <div className='campaign_cashback_grid'>
                        <div className='m-table__th campaign_cashback_grid_item'>内容</div>
                        <div className='m-table__td campaign_cashback_grid_item'>
                          {cashback.name}
                        </div>
                      </div>
                      <div className='campaign_cashback_grid'>
                        <div className='m-table__th campaign_cashback_grid_item'>お手続き状況</div>
                        <div className='m-table__td campaign_cashback_grid_item'>
                          {cashback.cashback_status_name}
                        </div>
                      </div>
                      <div className='campaign_cashback_grid'>
                        <div className='m-table__th campaign_cashback_grid_item'>
                          受取可能開始日
                        </div>
                        <div className='m-table__td campaign_cashback_grid_item'>
                          {formatDate(cashback.period.from)} 13:00
                        </div>
                      </div>
                      <div className='campaign_cashback_grid'>
                        <div className='m-table__th campaign_cashback_grid_item'>
                          受取可能終了日
                        </div>
                        <div className='m-table__td campaign_cashback_grid_item'>
                          {formatDate(cashback.period.to)}
                        </div>
                      </div>
                      <div>
                        {cashback.can_receive_cashback && (
                          <div className='m-button col1'>
                            <Button
                              variant='secondary'
                              onClick={() => toCashbackRegister(cashback.cashback_id)}
                            >
                              お手続き
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          <div className='m-button col2'>
            <RouterBackButton />
          </div>
        </div>
      </div>
      <BreadcrumbLists />
    </main>
  );
};

export default CampaignDetail;
